/**
 * Zip セッション上のサンプル CRUD（sample.service / storageRoom.service 互換の戻り値）。
 */
import type {
  Sample,
  SampleListParams,
  SampleListResponse,
  SampleLocationIndex,
  UsageRecord,
} from '../../sample.service';
import type { StorageRoom, StorageRoomListResponse } from '../../storageRoom.service';
import {
  emptySampleSnapshot,
  getSampleLocalSnapshot,
  mutateSampleLocalSnapshot,
  replaceSampleLocalSnapshot,
  sampleLocalGuestUserId,
  type SampleLocalSample,
  type SampleLocalSnapshot,
  type SampleLocalStorageRoom,
  type SampleLocalUsageRecord,
} from './sampleLocalDb';

const AVAILABLE = '利用可能';
const OUT_OF_STOCK = '在庫なし';
const DISPOSED = '廃棄済み';

function nowIso(): string {
  return new Date().toISOString();
}

function roomById(snap: SampleLocalSnapshot, id: number | null | undefined): SampleLocalStorageRoom | undefined {
  if (id == null) return undefined;
  return snap.storageRooms.find((r) => r.id === id);
}

function toSample(row: SampleLocalSample, snap: SampleLocalSnapshot, withUsage = false): Sample {
  const room = roomById(snap, row.storageRoomId ?? null);
  const usageRecords = withUsage
    ? snap.usageRecords
        .filter((u) => u.sampleId === row.id)
        .sort((a, b) => b.usageDate.localeCompare(a.usageDate))
        .map(
          (u): UsageRecord => ({
            id: u.id,
            sampleId: u.sampleId,
            userId: null,
            quantity: u.quantity,
            usageDate: u.usageDate,
            purpose: u.purpose,
            notes: u.notes,
            action: u.action,
            recordedBy: u.recordedBy,
            remainingQuantity: u.remainingQuantity,
            createdAt: u.createdAt,
          })
        )
    : undefined;
  return {
    id: row.id,
    userId: row.userId,
    storageRoomId: row.storageRoomId ?? undefined,
    name: row.name,
    sampleType: row.sampleType ?? undefined,
    bslLevel: row.bslLevel ?? undefined,
    infectiousDiseaseLaw: row.infectiousDiseaseLaw ?? undefined,
    initialQuantity: row.initialQuantity ?? undefined,
    remainingQuantity: row.remainingQuantity ?? undefined,
    unit: row.unit ?? undefined,
    storageDate: row.storageDate ?? undefined,
    expirationDate: row.expirationDate ?? undefined,
    notes: row.notes ?? undefined,
    pathogenGenus: row.pathogenGenus ?? undefined,
    pathogenSpecies: row.pathogenSpecies ?? undefined,
    pathogenSubspecies: row.pathogenSubspecies ?? undefined,
    pathogenStrain: row.pathogenStrain ?? undefined,
    pathogenNote: row.pathogenNote ?? undefined,
    livestockInfectiousDiseaseLaw: row.livestockInfectiousDiseaseLaw ?? undefined,
    drawerNumber: row.drawerNumber ?? undefined,
    drawerNote: row.drawerNote ?? undefined,
    caseName: row.caseName ?? undefined,
    lastUsedDate: row.lastUsedDate ?? undefined,
    disposalDate: row.disposalDate ?? undefined,
    creatorDisplayName: row.creatorDisplayName ?? 'Local',
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    usageRecords,
    storageRoom: room
      ? { id: room.id, name: room.name, location: room.location, type: room.type ?? undefined }
      : undefined,
  };
}

function toStorageRoom(row: SampleLocalStorageRoom, snap: SampleLocalSnapshot): StorageRoom {
  const samples = snap.samples
    .filter((s) => s.storageRoomId === row.id)
    .map((s) => ({ id: s.id, name: s.name }));
  return {
    id: row.id,
    name: row.name,
    location: row.location,
    type: row.type,
    isShared: row.isShared,
    userId: sampleLocalGuestUserId(),
    samples,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

function sampleStatus(row: SampleLocalSample): string {
  if (row.disposalDate) return DISPOSED;
  const rem = row.remainingQuantity ?? 0;
  if (rem <= 0) return OUT_OF_STOCK;
  return AVAILABLE;
}

function matchesListFilters(row: SampleLocalSample, snap: SampleLocalSnapshot, params: SampleListParams): boolean {
  if (params.status && sampleStatus(row) !== params.status) return false;
  if (params.storageRoomId != null && row.storageRoomId !== params.storageRoomId) return false;
  if (params.storageRoomIdIsNull && row.storageRoomId != null) return false;
  if (params.sampleType && row.sampleType !== params.sampleType) return false;
  if (params.bslLevel && row.bslLevel !== params.bslLevel) return false;
  if (params.name && !(row.name || '').toLowerCase().includes(params.name.toLowerCase())) return false;
  if (params.caseNameExact !== undefined) {
    if ((row.caseName ?? '') !== (params.caseNameExact ?? '')) return false;
  }
  if (params.caseNameIsNull && row.caseName != null && row.caseName !== '') return false;
  if (params.drawerNumberExact !== undefined && row.drawerNumber !== params.drawerNumberExact) return false;
  if (params.drawerNumberIsNull && row.drawerNumber != null) return false;
  if (params.pathogenGenus && !(row.pathogenGenus || '').includes(params.pathogenGenus)) return false;
  if (params.notesIsEmpty) {
    if ((row.notes || '').trim() !== '') return false;
  } else if (params.notes && !(row.notes || '').toLowerCase().includes(params.notes.toLowerCase())) {
    return false;
  }
  if (params.preparer) {
    const q = params.preparer.toLowerCase();
    if (!(row.creatorDisplayName || '').toLowerCase().includes(q)) return false;
  }
  if (params.storageRoom) {
    const room = roomById(snap, row.storageRoomId ?? null);
    const q = params.storageRoom.toLowerCase();
    const hay = `${room?.name || ''} ${room?.location || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (params.drawerNumber) {
    const q = String(params.drawerNumber).trim();
    if (row.drawerNumber == null || !String(row.drawerNumber).includes(q)) return false;
  }
  if (params.caseName) {
    const q = params.caseName.toLowerCase();
    if (!(row.caseName || '').toLowerCase().includes(q)) return false;
  }
  const createdYmd = (row.createdAt || '').slice(0, 10);
  if (params.createdDateStart && createdYmd && createdYmd < params.createdDateStart) return false;
  if (params.createdDateEnd && createdYmd && createdYmd > params.createdDateEnd) return false;
  const lastUsedYmd = (row.lastUsedDate || '').slice(0, 10);
  if (params.lastUsedDateStart) {
    if (!lastUsedYmd || lastUsedYmd < params.lastUsedDateStart) return false;
  }
  if (params.lastUsedDateEnd) {
    if (!lastUsedYmd || lastUsedYmd > params.lastUsedDateEnd) return false;
  }
  if (params.search) {
    const q = params.search.toLowerCase();
    const room = roomById(snap, row.storageRoomId ?? null);
    const hay = [
      row.name,
      row.notes,
      row.caseName,
      row.pathogenGenus,
      row.pathogenSpecies,
      row.creatorDisplayName,
      room?.name,
      room?.location,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

/** 最終使用日時の新しい順（未使用は後ろ）。同値は作製日・ID で安定化 */
function sortSamplesByLastUsedDesc(rows: SampleLocalSample[]): SampleLocalSample[] {
  return [...rows].sort((a, b) => {
    const ua = a.lastUsedDate || '';
    const ub = b.lastUsedDate || '';
    if (ua && ub && ua !== ub) return ub.localeCompare(ua);
    if (ua && !ub) return -1;
    if (!ua && ub) return 1;
    const ta = a.createdAt || '';
    const tb = b.createdAt || '';
    if (ta !== tb) return tb.localeCompare(ta);
    return b.id - a.id;
  });
}

/** ローカル暦の YYYY-MM-DD（toISOString の UTC ずれを避ける） */
function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function bumpCounter(snap: SampleLocalSnapshot, key: keyof SampleLocalSnapshot['counters']): number {
  const id = snap.counters[key];
  snap.counters[key] = id + 1;
  return id;
}

export const sampleLocalStore = {
  getSnapshot(): SampleLocalSnapshot {
    return getSampleLocalSnapshot();
  },

  async replaceSnapshot(next: SampleLocalSnapshot): Promise<void> {
    replaceSampleLocalSnapshot(next, { silent: true });
  },

  list(params: SampleListParams = {}): SampleListResponse {
    const snap = getSampleLocalSnapshot();
    const filtered = sortSamplesByLastUsedDesc(
      snap.samples.filter((s) => matchesListFilters(s, snap, params))
    );
    const page = Math.max(1, params.page ?? 1);
    const limit = Math.max(1, Math.min(500, params.limit ?? 50));
    const start = (page - 1) * limit;
    const slice = filtered.slice(start, start + limit);
    const stats = {
      total: snap.samples.length,
      available: snap.samples.filter((s) => sampleStatus(s) === AVAILABLE).length,
      outOfStock: snap.samples.filter((s) => sampleStatus(s) === OUT_OF_STOCK).length,
      disposed: snap.samples.filter((s) => sampleStatus(s) === DISPOSED).length,
    };
    return {
      success: true,
      data: slice.map((s) => toSample(s, snap)),
      pagination: {
        page,
        limit,
        total: filtered.length,
        totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
      },
      stats,
    };
  },

  getById(id: number): { success: boolean; data: Sample } {
    const snap = getSampleLocalSnapshot();
    const row = snap.samples.find((s) => s.id === id);
    if (!row) throw new Error('sample.not_found');
    return { success: true, data: toSample(row, snap, true) };
  },

  create(data: Partial<Sample>): { success: boolean; data: Sample } {
    let createdId = 0;
    mutateSampleLocalSnapshot((snap) => {
      const id = bumpCounter(snap, 'sample');
      createdId = id;
      const ts = nowIso();
      const initial = data.initialQuantity ?? data.remainingQuantity ?? 0;
      const row: SampleLocalSample = {
        id,
        userId: sampleLocalGuestUserId(),
        storageRoomId: data.storageRoomId ?? null,
        name: data.name || 'Sample',
        sampleType: data.sampleType ?? null,
        bslLevel: data.bslLevel ?? null,
        infectiousDiseaseLaw: data.infectiousDiseaseLaw ?? null,
        initialQuantity: initial,
        remainingQuantity: data.remainingQuantity ?? initial,
        unit: data.unit ?? null,
        storageDate: data.storageDate ?? null,
        expirationDate: data.expirationDate ?? null,
        notes: data.notes ?? null,
        pathogenGenus: data.pathogenGenus ?? null,
        pathogenSpecies: data.pathogenSpecies ?? null,
        pathogenSubspecies: data.pathogenSubspecies ?? null,
        pathogenStrain: data.pathogenStrain ?? null,
        pathogenNote: data.pathogenNote ?? null,
        livestockInfectiousDiseaseLaw: data.livestockInfectiousDiseaseLaw ?? null,
        drawerNumber: data.drawerNumber ?? null,
        drawerNote: data.drawerNote ?? null,
        caseName: data.caseName ?? null,
        lastUsedDate: null,
        disposalDate: data.disposalDate ?? null,
        creatorDisplayName: 'Local',
        createdAt: ts,
        updatedAt: ts,
      };
      snap.samples.push(row);
    });
    return this.getById(createdId);
  },

  update(id: number, data: Partial<Sample>): { success: boolean; data: Sample } {
    mutateSampleLocalSnapshot((snap) => {
      const row = snap.samples.find((s) => s.id === id);
      if (!row) throw new Error('sample.not_found');
      const keys: (keyof SampleLocalSample)[] = [
        'name',
        'storageRoomId',
        'sampleType',
        'bslLevel',
        'infectiousDiseaseLaw',
        'initialQuantity',
        'remainingQuantity',
        'unit',
        'storageDate',
        'expirationDate',
        'notes',
        'pathogenGenus',
        'pathogenSpecies',
        'pathogenSubspecies',
        'pathogenStrain',
        'pathogenNote',
        'livestockInfectiousDiseaseLaw',
        'drawerNumber',
        'drawerNote',
        'caseName',
        'disposalDate',
        'lastUsedDate',
      ];
      for (const key of keys) {
        if (key in data) {
          (row as Record<string, unknown>)[key] = (data as Record<string, unknown>)[key] ?? null;
        }
      }
      row.updatedAt = nowIso();
    });
    return this.getById(id);
  },

  delete(id: number): { success: boolean } {
    mutateSampleLocalSnapshot((snap) => {
      const before = snap.samples.length;
      snap.samples = snap.samples.filter((s) => s.id !== id);
      if (snap.samples.length === before) throw new Error('sample.not_found');
      snap.usageRecords = snap.usageRecords.filter((u) => u.sampleId !== id);
    });
    return { success: true };
  },

  bulkDelete(sampleIds: number[]): { success: boolean; data: { deleted: number; sampleIds: number[] }; message: string } {
    const set = new Set(sampleIds);
    let deleted = 0;
    mutateSampleLocalSnapshot((snap) => {
      const before = snap.samples.length;
      snap.samples = snap.samples.filter((s) => !set.has(s.id));
      deleted = before - snap.samples.length;
      snap.usageRecords = snap.usageRecords.filter((u) => !set.has(u.sampleId));
    });
    return { success: true, data: { deleted, sampleIds: [...set] }, message: 'ok' };
  },

  bulkDispose(sampleIds: number[]): { success: boolean; data: { disposed: number; sampleIds: number[] }; message: string } {
    const set = new Set(sampleIds);
    let disposed = 0;
    const ts = nowIso();
    mutateSampleLocalSnapshot((snap) => {
      for (const s of snap.samples) {
        if (set.has(s.id) && !s.disposalDate) {
          s.disposalDate = ts.slice(0, 10);
          s.remainingQuantity = 0;
          s.updatedAt = ts;
          disposed += 1;
        }
      }
    });
    return { success: true, data: { disposed, sampleIds: [...set] }, message: 'ok' };
  },

  addUsageRecord(
    sampleId: number,
    data: { quantity: number; usageDate: string; purpose?: string; notes?: string; action?: string; recordedBy?: string }
  ): { success: boolean; data: UsageRecord } {
    let created: SampleLocalUsageRecord | null = null;
    mutateSampleLocalSnapshot((snap) => {
      const sample = snap.samples.find((s) => s.id === sampleId);
      if (!sample) throw new Error('sample.not_found');
      if (sample.disposalDate) throw new Error('sample.already_disposed');
      const qty = Number(data.quantity) || 0;
      const rem = sample.remainingQuantity ?? 0;
      if (qty > rem) throw new Error('usage_record.quantity_exceeds_remaining');
      const nextRem = rem - qty;
      sample.remainingQuantity = nextRem;
      sample.lastUsedDate = data.usageDate;
      sample.updatedAt = nowIso();
      const id = bumpCounter(snap, 'usage');
      created = {
        id,
        sampleId,
        quantity: qty,
        usageDate: data.usageDate,
        purpose: data.purpose ?? null,
        notes: data.notes ?? null,
        action: data.action ?? '使用',
        recordedBy: data.recordedBy ?? null,
        remainingQuantity: nextRem,
        createdAt: nowIso(),
      };
      snap.usageRecords.push(created);
    });
    const u = created!;
    return {
      success: true,
      data: {
        id: u.id,
        sampleId: u.sampleId,
        userId: null,
        quantity: u.quantity,
        usageDate: u.usageDate,
        purpose: u.purpose,
        notes: u.notes,
        action: u.action,
        recordedBy: u.recordedBy,
        remainingQuantity: u.remainingQuantity,
        createdAt: u.createdAt,
      },
    };
  },

  bulkAddUsage(
    records: Array<{
      sampleId: number;
      quantity: number;
      usageDate: string;
      purpose?: string;
      notes?: string;
      action?: string;
    }>
  ): { success: boolean; data: { count: number; sampleIds: number[] }; message: string } {
    const ids: number[] = [];
    mutateSampleLocalSnapshot((snap) => {
      // 先に全件検証（途中失敗で部分適用しない）
      for (const r of records) {
        const sample = snap.samples.find((s) => s.id === r.sampleId);
        if (!sample) throw new Error('sample.not_found');
        if (sample.disposalDate) throw new Error('sample.already_disposed');
        const qty = Number(r.quantity) || 0;
        const rem = sample.remainingQuantity ?? 0;
        if (qty > rem) throw new Error('usage_record.quantity_exceeds_remaining');
      }
      for (const r of records) {
        const sample = snap.samples.find((s) => s.id === r.sampleId)!;
        const qty = Number(r.quantity) || 0;
        const rem = sample.remainingQuantity ?? 0;
        const nextRem = rem - qty;
        sample.remainingQuantity = nextRem;
        sample.lastUsedDate = r.usageDate;
        sample.updatedAt = nowIso();
        const id = bumpCounter(snap, 'usage');
        snap.usageRecords.push({
          id,
          sampleId: r.sampleId,
          quantity: qty,
          usageDate: r.usageDate,
          purpose: r.purpose ?? null,
          notes: r.notes ?? null,
          action: r.action ?? '使用',
          recordedBy: null,
          remainingQuantity: nextRem,
          createdAt: nowIso(),
        });
        ids.push(r.sampleId);
      }
    });
    return { success: true, data: { count: records.length, sampleIds: ids }, message: 'ok' };
  },

  getLocationIndex(): { success: boolean; data: SampleLocationIndex } {
    const snap = getSampleLocalSnapshot();
    const byRoom = new Map<number, SampleLocalSample[]>();
    const without: SampleLocalSample[] = [];
    for (const s of snap.samples) {
      if (s.storageRoomId == null) without.push(s);
      else {
        const list = byRoom.get(s.storageRoomId) || [];
        list.push(s);
        byRoom.set(s.storageRoomId, list);
      }
    }

    const buildDrawers = (samples: SampleLocalSample[]) => {
      const drawerMap = new Map<number | null, SampleLocalSample[]>();
      for (const s of samples) {
        const key = s.drawerNumber ?? null;
        const list = drawerMap.get(key) || [];
        list.push(s);
        drawerMap.set(key, list);
      }
      return [...drawerMap.entries()].map(([drawerNumber, ds]) => {
        const caseMap = new Map<string | null, SampleLocalSample[]>();
        for (const s of ds) {
          const ck = s.caseName ?? null;
          const list = caseMap.get(ck) || [];
          list.push(s);
          caseMap.set(ck, list);
        }
        return {
          drawerNumber,
          sampleCount: ds.length,
          totalRemaining: ds.reduce((a, s) => a + (s.remainingQuantity ?? 0), 0),
          cases: [...caseMap.entries()].map(([caseName, cs]) => ({
            caseName,
            sampleCount: cs.length,
            totalRemaining: cs.reduce((a, s) => a + (s.remainingQuantity ?? 0), 0),
          })),
        };
      });
    };

    const storageRooms = snap.storageRooms.map((room) => {
      const samples = byRoom.get(room.id) || [];
      return {
        id: room.id,
        name: room.name,
        location: room.location,
        sampleCount: samples.length,
        totalRemaining: samples.reduce((a, s) => a + (s.remainingQuantity ?? 0), 0),
        isShared: room.isShared,
        drawers: buildDrawers(samples),
      };
    });

    return {
      success: true,
      data: {
        storageRooms,
        withoutStorageRoom:
          without.length > 0
            ? {
                sampleCount: without.length,
                totalRemaining: without.reduce((a, s) => a + (s.remainingQuantity ?? 0), 0),
                drawers: buildDrawers(without),
              }
            : null,
      },
    };
  },

  listStorageRooms(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): StorageRoomListResponse {
    const snap = getSampleLocalSnapshot();
    let rows = snap.storageRooms;
    if (params?.search) {
      const q = params.search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.location || '').toLowerCase().includes(q) ||
          (r.type || '').toLowerCase().includes(q)
      );
    }
    const page = Math.max(1, params?.page ?? 1);
    const limit = Math.max(1, Math.min(500, params?.limit ?? 50));
    const start = (page - 1) * limit;
    const slice = rows.slice(start, start + limit);
    return {
      success: true,
      data: slice.map((r) => toStorageRoom(r, snap)),
      pagination: {
        page,
        limit,
        total: rows.length,
        totalPages: Math.max(1, Math.ceil(rows.length / limit)),
      },
    };
  },

  getStorageRoom(id: number): { success: boolean; data: StorageRoom } {
    const snap = getSampleLocalSnapshot();
    const row = snap.storageRooms.find((r) => r.id === id);
    if (!row) throw new Error('sample.storage_room_not_found');
    return { success: true, data: toStorageRoom(row, snap) };
  },

  createStorageRoom(data: {
    name: string;
    location?: string | null;
    type?: string | null;
    isShared?: boolean;
  }): { success: boolean; data: StorageRoom } {
    let id = 0;
    mutateSampleLocalSnapshot((snap) => {
      id = bumpCounter(snap, 'storageRoom');
      const ts = nowIso();
      snap.storageRooms.push({
        id,
        name: data.name,
        location: data.location ?? null,
        type: data.type ?? null,
        isShared: !!data.isShared,
        createdAt: ts,
        updatedAt: ts,
      });
    });
    return this.getStorageRoom(id);
  },

  updateStorageRoom(
    id: number,
    data: { name?: string; location?: string | null; type?: string | null; isShared?: boolean }
  ): { success: boolean; data: StorageRoom } {
    mutateSampleLocalSnapshot((snap) => {
      const row = snap.storageRooms.find((r) => r.id === id);
      if (!row) throw new Error('sample.storage_room_not_found');
      if (data.name !== undefined) row.name = data.name;
      if (data.location !== undefined) row.location = data.location;
      if (data.type !== undefined) row.type = data.type;
      if (data.isShared !== undefined) row.isShared = data.isShared;
      row.updatedAt = nowIso();
    });
    return this.getStorageRoom(id);
  },

  deleteStorageRoom(id: number): { success: boolean } {
    mutateSampleLocalSnapshot((snap) => {
      if (snap.samples.some((s) => s.storageRoomId === id)) {
        throw new Error('sample.storage_room_has_samples');
      }
      const before = snap.storageRooms.length;
      snap.storageRooms = snap.storageRooms.filter((r) => r.id !== id);
      if (snap.storageRooms.length === before) throw new Error('sample.storage_room_not_found');
    });
    return { success: true };
  },

  getDashboardStats(): {
    total_samples: number;
    available_samples: number;
    out_of_stock_samples: number;
    disposed_samples: number;
    total_storage_rooms: number;
    activity_data: Array<{
      id: string;
      sample_id: number;
      sample_name: string;
      kind: 'usage' | 'registered';
      created_at: string;
      quantity?: number;
      notes?: string | null;
      creator_name?: string;
    }>;
    chart_data: Array<{ month: string; count: number }>;
  } {
    const snap = getSampleLocalSnapshot();
    const chartMap = new Map<string, number>();
    for (const s of snap.samples) {
      const month = (s.createdAt || '').slice(0, 7);
      if (!month) continue;
      chartMap.set(month, (chartMap.get(month) || 0) + 1);
    }
    const activity_data = [
      ...snap.usageRecords.map((u) => {
        const sample = snap.samples.find((s) => s.id === u.sampleId);
        return {
          id: `u-${u.id}`,
          sample_id: u.sampleId,
          sample_name: sample?.name || `#${u.sampleId}`,
          kind: 'usage' as const,
          created_at: u.usageDate,
          quantity: u.quantity,
          notes: u.notes,
          creator_name: u.recordedBy || 'Local',
        };
      }),
      ...snap.samples.map((s) => ({
        id: `s-${s.id}`,
        sample_id: s.id,
        sample_name: s.name,
        kind: 'registered' as const,
        created_at: s.createdAt,
        creator_name: s.creatorDisplayName || 'Local',
      })),
    ]
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, 50);

    return {
      total_samples: snap.samples.length,
      available_samples: snap.samples.filter((s) => sampleStatus(s) === AVAILABLE).length,
      out_of_stock_samples: snap.samples.filter((s) => sampleStatus(s) === OUT_OF_STOCK).length,
      disposed_samples: snap.samples.filter((s) => sampleStatus(s) === DISPOSED).length,
      total_storage_rooms: snap.storageRooms.length,
      activity_data,
      chart_data: [...chartMap.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, count]) => ({ month, count })),
    };
  },

  /**
   * 使用記録（日別）。基準日から遡る期間内の記録を返す（Zip ローカル）。
   */
  getUsageRecordsByDate(params?: {
    baseDate?: string;
    filters?: { keyword?: string };
  }): {
    baseDate: string;
    periodStart: string;
    periodEnd: string;
    days: Array<{
      date: string;
      records: Array<{
        id: number;
        sampleId: number;
        sampleName: string;
        sampleType: string | null;
        bslLevel: string | null;
        unit?: string | null;
        storageRoomName: string | null;
        storageRoomLocation: string | null;
        drawerNumber: number | null;
        caseName: string | null;
        creatorName: string | null;
        action: string | null;
        quantity: number;
        remainingQuantity: number | null;
        purpose: string | null;
        notes: string | null;
        recordedBy: string | null;
        usageDate: string;
      }>;
    }>;
    totalRecords: number;
  } {
    const snap = getSampleLocalSnapshot();
    const baseDate = (params?.baseDate || localYmd(new Date())).slice(0, 10);
    const end = new Date(`${baseDate}T12:00:00`);
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    const periodStart = localYmd(start);
    const q = (params?.filters?.keyword || '').trim().toLowerCase();

    const rows = snap.usageRecords
      .filter((u) => {
        const d = (u.usageDate || '').slice(0, 10);
        return d >= periodStart && d <= baseDate;
      })
      .map((u) => {
        const sample = snap.samples.find((s) => s.id === u.sampleId);
        const room = sample ? roomById(snap, sample.storageRoomId ?? null) : undefined;
        return {
          id: u.id,
          sampleId: u.sampleId,
          sampleName: sample?.name || `#${u.sampleId}`,
          sampleType: sample?.sampleType ?? null,
          bslLevel: sample?.bslLevel ?? null,
          unit: sample?.unit ?? null,
          storageRoomName: room?.name ?? null,
          storageRoomLocation: room?.location ?? null,
          drawerNumber: sample?.drawerNumber ?? null,
          caseName: sample?.caseName ?? null,
          creatorName: u.recordedBy || sample?.creatorDisplayName || 'Local',
          action: u.action ?? null,
          quantity: u.quantity,
          remainingQuantity: u.remainingQuantity ?? null,
          purpose: u.purpose ?? null,
          notes: u.notes ?? null,
          recordedBy: u.recordedBy ?? null,
          usageDate: u.usageDate,
        };
      })
      .filter((row) => {
        if (!q) return true;
        const hay = [row.sampleName, row.notes, row.purpose, row.storageRoomName, row.caseName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => b.usageDate.localeCompare(a.usageDate));

    const byDate = new Map<string, typeof rows>();
    for (const row of rows) {
      const d = row.usageDate.slice(0, 10);
      const list = byDate.get(d) || [];
      list.push(row);
      byDate.set(d, list);
    }

    const days = [...byDate.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, records]) => ({ date, records }));

    return {
      baseDate,
      periodStart,
      periodEnd: baseDate,
      days,
      totalRecords: rows.length,
    };
  },

  /** テスト／初期化用 */
  resetEmpty(): void {
    replaceSampleLocalSnapshot(emptySampleSnapshot(), { silent: true });
  },
};
